import api from './api';

export type EventType = 'borrel' | 'lunch' | 'wandeling' | 'grab_a_coffee' | 'samen_werken' | 'anders';

export const AUDIENCE_OPTIONS = [
  { value: 'alle_professionals', label: 'Alle professionals' },
  { value: 'bmw', label: 'Bedrijfsmaatschappelijk werker (BMW)' },
  { value: 'vertrouwenspersoon', label: 'Vertrouwenspersoon' },
  { value: 'mediator', label: 'Mediator' },
  { value: 'loopbaancoach', label: 'Loopbaancoach' },
  { value: 'teamcoach', label: 'Teamcoach' },
  { value: 'vitaliteitscoach', label: 'Vitaliteitscoach' },
  { value: 'burnout_coach', label: 'Burnout coach' },
  { value: 'psycholoog_coach', label: 'Psycholoog/coach' },
  { value: 'financieel_coach', label: 'Financieel coach' },
] as const;

export type AudienceValue = (typeof AUDIENCE_OPTIONS)[number]['value'];

export interface EventDateTime {
  date: string;      // ISO date string
  time_from: string; // HH:MM
  time_to?: string;  // HH:MM
}

export interface ConnectMeetEvent {
  id: string;
  creator_id: string;
  creator_name: string;
  type: EventType;
  type_custom?: string;
  description?: string;
  image_url?: string;
  event_date: string; // ISO date string
  time_from: string;  // HH:MM
  time_to?: string;   // HH:MM
  extra_dates?: EventDateTime[];
  location: string;
  regio: string;
  regios?: string[];
  city?: string;
  audience?: string[];
  max_persons?: number;
  attendee_count: number;
  is_attending: boolean;
  is_creator: boolean;
  is_full: boolean;
  attendees?: ConnectMeetAttendee[];
  created_at: string;
}

export interface ConnectMeetAttendee {
  user_id: string;
  first_name: string;
  last_name: string;
  joined_at: string;
  comment?: string | null;
}

export interface CreateEventData {
  type: EventType;
  type_custom?: string;
  description?: string;
  image_url?: string;
  event_date: string;
  time_from: string;
  time_to?: string;
  extra_dates?: EventDateTime[];
  location: string;
  regio: string;
  regios?: string[];
  city?: string;
  audience?: string[];
  max_persons?: number;
}

export interface Professional {
  id: string;
  first_name: string;
  last_name: string;
  regio?: string;
}

export const connectMeetApi = {
  uploadImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await api.post('/connect-meet/upload-image', formData);
    return res.data.url;
  },

  getEvents: async (params?: { regio?: string; type?: string; city?: string }): Promise<ConnectMeetEvent[]> => {
    const res = await api.get('/connect-meet/', { params });
    return res.data.data;
  },

  getEvent: async (id: string): Promise<ConnectMeetEvent> => {
    const res = await api.get(`/connect-meet/${id}`);
    return res.data.data;
  },

  createEvent: async (data: CreateEventData): Promise<ConnectMeetEvent> => {
    // Send arrays as JSON strings so the backend parseArray helper can decode them
    const payload: Record<string, unknown> = { ...data };
    if (data.audience) payload.audience = JSON.stringify(data.audience);
    if (data.regios) payload.regios = JSON.stringify(data.regios);
    if (data.extra_dates) payload.extra_dates = JSON.stringify(data.extra_dates);
    const res = await api.post('/connect-meet/', payload);
    return res.data.data;
  },

  updateEvent: async (id: string, data: Partial<CreateEventData>): Promise<ConnectMeetEvent> => {
    const payload: Record<string, unknown> = { ...data };
    if (data.audience) payload.audience = JSON.stringify(data.audience);
    if (data.regios) payload.regios = JSON.stringify(data.regios);
    if (data.extra_dates) payload.extra_dates = JSON.stringify(data.extra_dates);
    const res = await api.put(`/connect-meet/${id}`, payload);
    return res.data.data;
  },

  deleteEvent: async (id: string): Promise<void> => {
    await api.delete(`/connect-meet/${id}`);
  },

  toggleAttend: async (id: string, comment?: string): Promise<{ attending: boolean; attendee_count: number }> => {
    const res = await api.post(`/connect-meet/${id}/attend`, { comment });
    return res.data;
  },

  inviteProfessional: async (eventId: string, userId: string): Promise<void> => {
    await api.post(`/connect-meet/${eventId}/invite`, { user_id: userId });
  },

  searchProfessionals: async (search: string, regio?: string): Promise<Professional[]> => {
    const res = await api.get('/connect-meet/professionals', { params: { search, regio } });
    return res.data.data;
  },

  getMyEvents: async (): Promise<ConnectMeetEvent[]> => {
    const res = await api.get('/connect-meet/my-events');
    return res.data.data;
  },

  getRegionSubscriptions: async (): Promise<string[]> => {
    const res = await api.get('/connect-meet/region-subscriptions');
    return res.data.data;
  },

  subscribeToRegion: async (regio: string): Promise<string[]> => {
    const res = await api.post('/connect-meet/region-subscriptions', { regio });
    return res.data.data;
  },

  unsubscribeFromRegion: async (regio: string): Promise<void> => {
    await api.delete(`/connect-meet/region-subscriptions/${encodeURIComponent(regio)}`);
  },
};
